import axios from 'axios';
import { ArrowLeft, Loader2, Plus, Save } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import type { AnalyticsDateRange } from '@/types/analytics';
import type { ReportRegistry, UserDashboardSummary, WidgetConfig } from '@/types/dashboards';

import WidgetCanvas from './WidgetCanvas';
import WidgetChart from './WidgetChart';
import WidgetConfigSheet from './WidgetConfigSheet';
import { useWidgetPreview } from './useWidgetPreview';

interface DashboardBuilderProps {
    registry: ReportRegistry;
    dateRange: AnalyticsDateRange;
    initial?: { id: string; name: string; widgets: WidgetConfig[] } | null;
    onSaved: (dashboard: UserDashboardSummary) => void;
    onCancel: () => void;
}

/** A single builder-canvas card body that fetches its own live preview. */
function LiveWidgetBody({ widget, dateRange }: { widget: WidgetConfig; dateRange: AnalyticsDateRange }) {
    const { t } = useTranslations();
    const chartHeightClass = useChartHeightClass('md');
    // KPI tiles render compact (no chart axes to fit), so their loading/empty
    // placeholder shouldn't reserve full chart height either.
    const heightClass = widget.chart_type === 'kpi' ? 'h-16' : chartHeightClass;
    const { result, loading } = useWidgetPreview(widget, dateRange);

    if (loading && !result) {
        return (
            <div className={cn('text-muted-foreground flex items-center justify-center', heightClass)}>
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }

    if (!result) {
        return (
            <div className={cn('text-muted-foreground flex items-center justify-center text-sm', heightClass)}>
                {t('dashboards.builder.no_data', 'No data for the selected period.')}
            </div>
        );
    }

    return <WidgetChart result={result} />;
}

export default function DashboardBuilder({ registry, dateRange, initial, onSaved, onCancel }: DashboardBuilderProps) {
    const { t } = useTranslations();
    const [name, setName] = useState(initial?.name ?? '');
    const [widgets, setWidgets] = useState<WidgetConfig[]>(initial?.widgets ?? []);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [editingWidget, setEditingWidget] = useState<WidgetConfig | null>(null);
    const [saving, setSaving] = useState(false);

    const openAddWidget = () => {
        setEditingWidget(null);
        setSheetOpen(true);
    };

    const openEditWidget = (widget: WidgetConfig) => {
        setEditingWidget(widget);
        setSheetOpen(true);
    };

    const upsertWidget = (widget: WidgetConfig) => {
        setWidgets((prev) => (prev.some((w) => w.id === widget.id) ? prev.map((w) => (w.id === widget.id ? widget : w)) : [...prev, widget]));
    };

    const removeWidget = (id: string) => setWidgets((prev) => prev.filter((w) => w.id !== id));

    const save = async () => {
        if (!name.trim()) {
            toast.error(t('dashboards.builder.name_required', 'Give your dashboard a name first.'));
            return;
        }
        if (widgets.length === 0) {
            toast.error(t('dashboards.builder.widgets_required', 'Add at least one widget.'));
            return;
        }

        setSaving(true);
        try {
            const payload = { name: name.trim(), widgets };
            const response = initial?.id
                ? await axios.put(route('dashboards.update', initial.id), payload)
                : await axios.post(route('dashboards.store'), payload);

            toast.success(t('dashboards.builder.saved', 'Dashboard saved.'));
            onSaved({ id: response.data.id, name: response.data.name });
        } catch {
            toast.error(t('dashboards.builder.save_failed', 'Could not save the dashboard.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex items-end gap-3">
                    <Button variant="ghost" size="icon" onClick={onCancel} aria-label={t('common.back', 'Back')}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="space-y-1">
                        <Label htmlFor="dashboard-name">{t('dashboards.builder.dashboard_name', 'Dashboard name')}</Label>
                        <Input
                            id="dashboard-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('dashboards.builder.name_placeholder', 'e.g. Sales overview')}
                            className="w-64"
                        />
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={openAddWidget}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('dashboards.builder.add_widget', 'Add widget')}
                    </Button>
                    <Button onClick={save} disabled={saving}>
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        {t('dashboards.builder.save', 'Save dashboard')}
                    </Button>
                </div>
            </div>

            {widgets.length === 0 ? (
                <Button
                    type="button"
                    variant="outline"
                    onClick={openAddWidget}
                    className="text-muted-foreground flex h-auto min-h-[240px] w-full flex-col items-center justify-center gap-2 rounded-xl border-dashed text-sm"
                >
                    <Plus className="h-6 w-6" />
                    {t('dashboards.builder.add_first_widget', 'Add your first widget')}
                </Button>
            ) : (
                <WidgetCanvas
                    widgets={widgets}
                    editable
                    onReorder={setWidgets}
                    onEdit={openEditWidget}
                    onRemove={removeWidget}
                    renderBody={(widget) => <LiveWidgetBody widget={widget} dateRange={dateRange} />}
                />
            )}

            <WidgetConfigSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                registry={registry}
                dateRange={dateRange}
                initialWidget={editingWidget}
                onSave={upsertWidget}
            />
        </div>
    );
}
